import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Typography,
  CircularProgress,
  Skeleton,
  styled,
  Alert,
  TextField,
} from "@mui/material";

import { Fragment, useCallback, useMemo, useState } from "react";

import { Add, ArrowBackIos, ArrowForwardIos, DataUsage, Edit, Save } from "@mui/icons-material";
import dayjs from "dayjs";
import ApiService from "@/api/ApiService";
import { dataTagSymbol, queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { MainOrder, ManualOrder } from "./MixingPlantView";
import { NewExternalOrder } from "./AddNewExternalDialog";
import { DataGridPro, DataGridProProps, GridColDef, GridRowClassNameParams } from "@mui/x-data-grid-pro";
import { CalendarX, Plus } from "lucide-react";
import StyledModal from "@/components/planning/dialogs/newMaterialOrder/StyledModal";
import useRights from "@/shared/api/useRights";
import ManyToOneSelect from "@/shared/forms/ManyToOneSelect";
import { LoadingButton } from "@mui/lab";
import NewRangePicker from "@/shared/forms/NewRangePicker";
import LabelWrapper from "@/components/root/LabelWrapper";
import DeleteButton from "@/shared/dialogs/DeleteButton";

const getTreeDataPath = (row) => row.path.split("###");

const StyledDataGrid = styled(DataGridPro)(({ theme }) => ({
  "& .auslastung_level1": {
    backgroundColor: "#fff",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 99%)",
    },
  },
  "& .auslastung_level2": {
    backgroundColor: "hsl(227, 100%, 98%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 97%)",
    },
  },
  "& .auslastung_level3": {
    backgroundColor: "hsl(227, 100%, 96%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 95%)",
    },
  },
  "& .auslastung_level4": {
    backgroundColor: "hsl(227, 100%, 94%)",
    "&:hover": {
      backgroundColor: "hsl(227, 100%, 93%)",
    },
  },
}));

interface Schliesstag {
  id: number;
  start: string;
  end: string;
  comment: string;
  plant?: number;
}

export default function Schliesstage({ allowedPlant }: { allowedPlant: number }) {
  const [open, setOpen] = useState(false);
  const [editorState, setEditorState] = useState<Schliesstag | null>(null);
  const [currentPlant, setCurrentPlant] = useState(allowedPlant < 1 ? 0 : allowedPlant);

  const close = useCallback(() => {
    setOpen(false);
    setEditorState(null);
  }, []);

  const query = useQuery<Schliesstag[]>({
    queryFn: ApiService.getEntitiesWithFilter("closingPeriod", { deletedAt: null, plant: ["plant", currentPlant] }),
    queryKey: ["getClosingPeriods", "" + currentPlant],
    enabled: currentPlant > 0,
  });

  const queryClient = useQueryClient();

  const saveMutation = useMutation<Schliesstag, AxiosResponse, Schliesstag>({
    mutationFn: ApiService.editEntity("closingPeriod"),
    onSuccess: (answer) => {
      queryClient.invalidateQueries({ queryKey: ["getClosingPeriods"] });
      setEditorState(null);
    },
  });

  const createMutation = useMutation<Schliesstag, AxiosResponse, Schliesstag>({
    mutationFn: ApiService.createEntity("closingPeriod"),
    onSuccess: (answer) => {
      queryClient.invalidateQueries({ queryKey: ["getClosingPeriods"] });
      setEditorState(null);
    },
  });

  const filteredST = useMemo(() => {
    const today = dayjs().format("YYYY-MM-DD");
    return (query.data ?? []).filter((s) => s.end >= today).sort((a, b) => a.start.localeCompare(b.start));
  }, [query.data]);

  return (
    <Fragment>
      <Button startIcon={<CalendarX />} variant="outlined" size="small" onClick={() => setOpen(true)}>
        Schließtage
      </Button>
      {open ? (
        <StyledModal
          open={true}
          onClose={!!editorState ? undefined : close}
          onBack={!!editorState ? () => setEditorState(null) : undefined}
          onShow={() => {}}
        >
          {!editorState ? (
            <Fragment>
              <Typography variant="h6">Schließtage</Typography>
              <ManyToOneSelect
                entity="plant"
                sx={{ marginBottom: "10px" }}
                v="calendar"
                disabled={allowedPlant !== -1}
                placeholder="Lieferwerk wählen..."
                currentId={currentPlant}
                setId={setCurrentPlant}
                autoSelect
                remoteFilter={{ deletedAt: null, showInList: true }}
              />
              {currentPlant < 1 ? (
                <Alert severity="info">Bitte wähle erst ein Lieferwerk</Alert>
              ) : filteredST.length === 0 ? (
                <Alert severity="info">Keine Schließtage gefunden</Alert>
              ) : (
                <Box>
                  {filteredST.map((st) => (
                    <Box
                      key={st.id}
                      sx={{
                        display: "flex",
                        gap: "10px",
                        p: "5px",
                        "&:first-child": { borderTop: "1px solid #aaa" },
                        borderBottom: "1px solid #aaa",
                      }}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "5px", flex: 1 }}>
                        <Box>
                          {dayjs(st.start).format("ddd, DD.MM.YYYY")}
                          {st.start !== st.end ? dayjs(st.end).format("[ bis ]ddd, DD.MM.YYYY") : null}
                        </Box>
                        {st.comment.length > 0 ? <Box sx={{ fontSize: "90%", opacity: 0.9 }}>{st.comment}</Box> : null}
                      </Box>
                      <Box>
                        <IconButton size="small" onClick={() => setEditorState(st)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <DeleteButton type="closingPeriod" xsx={{}} id={st.id} invalidate={["getClosingPeriods"]} size="small" fontSize="small" />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
              <Box sx={{ mt: "10px", display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    setEditorState({
                      id: 0,
                      plant: currentPlant,
                      start: dayjs().add(1, "week").format("YYYY-MM-DD"),
                      end: dayjs().add(1, "week").format("YYYY-MM-DD"),
                      comment: "",
                    })
                  }
                  startIcon={<Add />}
                >
                  Neuer Schließtag
                </Button>
              </Box>
            </Fragment>
          ) : (
            <Fragment>
              <Typography variant="h6">{editorState.id === 0 ? "Schließtage hinzufügen" : "Schließtage bearbeiten"}</Typography>
              <LabelWrapper label="Zeitraum">
                <NewRangePicker
                  small
                  onChange={([startDate, endDate]) =>
                    setEditorState((es) =>
                      !es
                        ? es
                        : {
                            ...es,
                            start: !!startDate ? startDate.format("YYYY-MM-DD") : null,
                            end: !!endDate ? endDate.format("YYYY-MM-DD") : null,
                          },
                    )
                  }
                  value={[!!editorState.start ? dayjs(editorState.start) : null, !!editorState.end ? dayjs(editorState.end) : null]}
                />
              </LabelWrapper>
              <LabelWrapper label="Kommentar">
                <TextField
                  size="small"
                  fullWidth
                  multiline
                  value={editorState.comment}
                  placeholder="Kommentar"
                  onChange={(e) =>
                    setEditorState((es) =>
                      !es
                        ? es
                        : {
                            ...es,
                            comment: (e.target as HTMLInputElement).value,
                          },
                    )
                  }
                />
              </LabelWrapper>
              <Box sx={{ mt: "10px", gap: "10px", display: "flex", justifyContent: "flex-end" }}>
                <Button onClick={() => setEditorState(null)}>Abbrechen</Button>
                <LoadingButton
                  loading={saveMutation.isPending || createMutation.isPending}
                  variant="outlined"
                  onClick={() => (editorState.id === 0 ? createMutation.mutate(editorState) : saveMutation.mutate(editorState))}
                  startIcon={<Save />}
                >
                  Speichern
                </LoadingButton>
              </Box>
            </Fragment>
          )}
        </StyledModal>
      ) : null}
    </Fragment>
  );
}
